// src/hooks/useEventConfig.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  carregarEventConfig, CONFIG_PADRAO, EventConfig, lerCache, urlDoLogo,
} from '../api/eventConfig'

interface Contexto {
  config: EventConfig
  logo: string | null
  carregando: boolean
  recarregar: () => void
}

const EventConfigContext = createContext<Contexto>({
  config: CONFIG_PADRAO,
  logo: null,
  carregando: true,
  recarregar: () => undefined,
})

/**
 * Disponibiliza a identidade visual do evento para toda a aplicação.
 *
 * Parte do cache local, então a primeira pintura já sai com as cores certas — sem o
 * flash de tela branca que apareceria esperando a rede. A revalidação acontece em
 * segundo plano e só custa um 304 quando nada mudou.
 */
export const EventConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<EventConfig>(() => lerCache() || CONFIG_PADRAO)
  const [carregando, setCarregando] = useState(true)
  const [gatilho, setGatilho] = useState(0)

  useEffect(() => {
    let vivo = true
    carregarEventConfig()
      .then(c => {
        if (vivo) setConfig(c)
      })
      .finally(() => {
        if (vivo) setCarregando(false)
      })
    return () => {
      vivo = false
    }
  }, [gatilho])

  // Expõe as cores como CSS custom properties: componentes fora do Chakra
  // (a folha de impressão, por exemplo) usam as mesmas variáveis.
  useEffect(() => {
    const b = config.branding || {}
    const raiz = document.documentElement.style
    raiz.setProperty('--evento-primaria', b.primaryColor || '#092D77')
    raiz.setProperty('--evento-secundaria', b.secondaryColor || '#38B7B4')
    raiz.setProperty('--evento-destaque', b.accentColor || '#235C78')
    raiz.setProperty('--evento-fundo', b.backgroundColor || '#FFFFFF')
    document.title = config.shortName || config.name || 'Autoatendimento'
  }, [config])

  const valor = useMemo<Contexto>(
    () => ({
      config,
      logo: urlDoLogo(config),
      carregando,
      recarregar: () => setGatilho(g => g + 1),
    }),
    [config, carregando],
  )

  return <EventConfigContext.Provider value={valor}>{children}</EventConfigContext.Provider>
}

export const useEventConfig = () => useContext(EventConfigContext)
